import React from "react";
import styled from "styled-components";
import Colors from '../../ui/global/Colors';
import Fonts from '../../ui/global/Fonts';

const CardsContainer = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
  gap: 20px;
  max-width: 1200px;
  
  @media (max-width: 1000px) {
    grid-template-columns: 1fr;
    max-width: 100%;
    padding: 0 10px;
  }
`;

const Card = styled.div<{ $colorMode: 'light' | 'dark' }>`
  display: flex;
  flex-direction: column;
  width: 100%;
  border-radius: 12px;
  border: 1px solid ${props => props.$colorMode === 'light' ? Colors.grey1 : Colors.grey8};
  overflow: hidden;
  background-color: ${props => props.$colorMode === 'light' ? Colors.white+60 : Colors.grayDark+80};
  line-height: 1.2em;
  padding: 10px 20px;
  gap: 6px;
  // box-shadow: 0 4px 16px rgba(0, 0, 0, 0.08);
`;

const CardHeader = styled.div<{ $colorMode: 'light' | 'dark' }>`
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 8px 8px 0 0;
  // background-color: ${props => props.$colorMode === 'light' ? Colors.white : Colors.black};
  border-bottom: 1px solid ${props => props.$colorMode === 'light' ? Colors.grayPale : Colors.grey8+80};
  padding: 12px 10px 22px 10px;
`;

const CardTitle = styled.h3<{ $colorMode: 'light' | 'dark' }>`
  font-family: ${Fonts.lexendSemiBold.fontFamily};
  font-weight: ${Fonts.lexendSemiBold.fontWeight};
  font-size: 24px;
  color: ${props => props.$colorMode === 'light' ? Colors.grey8 : Colors.grey1};
  margin: 0;
  text-align: center;
`;

const CardContent = styled.div`
  display: flex;
  flex-direction: column;
  padding: 16px 0;
  gap: 10px;
`;

const ItemRow = styled.div<{ $colorMode: 'light' | 'dark' }>`
  display: flex;
  justify-content: space-between;
  align-items: baseline;
  font-size: 16px;
  color: ${props => props.$colorMode === 'light' ? Colors.grey7 : Colors.grey3};
  gap: 18px;
`;

const ItemName = styled.span<{ $colorMode: 'light' | 'dark' }>`
  font-family: ${Fonts.lexendSemiLight.fontFamily};
  font-weight: ${Fonts.lexendSemiLight.fontWeight};
`;

const ItemValue = styled.span<{ $colorMode: 'light' | 'dark' }>`
  font-family: ${Fonts.lexendSemiBold.fontFamily};
  font-weight: ${Fonts.lexendSemiBold.fontWeight};
  display: flex;
  align-items: baseline;
`;

const CreditUnit = styled.span<{ $colorMode: 'light' | 'dark' }>`
  font-family: ${Fonts.lexendLight.fontFamily};
  font-weight: ${Fonts.lexendLight.fontWeight};
  margin-left: 8px;
`;

interface CreditItem {
  type: string;
  item: string;
  value: number;
  unit: string;
}

interface CreditsGridProps {
  creditItems: CreditItem[];
  colorMode?: 'light' | 'dark';
}

const CreditsGrid = ({ creditItems, colorMode = 'dark' }: CreditsGridProps) => {
  // Group items by type
  const groupedByType: { [key: string]: CreditItem[] } = {};
  creditItems.forEach(item => {
    if (!groupedByType[item.type]) {
      groupedByType[item.type] = [];
    }
    groupedByType[item.type].push(item);
  });

  return (
    <CardsContainer>
      {Object.entries(groupedByType).map(([type, items]) => (
        <Card key={type} $colorMode={colorMode}>
          <CardHeader $colorMode={colorMode}>
            <CardTitle $colorMode={colorMode}>{type}</CardTitle>
          </CardHeader>
          <CardContent>
            {items.map((item) => (
              <ItemRow key={`${type}-${item.item}`} $colorMode={colorMode}>
                <ItemName $colorMode={colorMode}>
                  {item.item}
                </ItemName>
                <ItemValue $colorMode={colorMode}>
                  {item.value}
                  <CreditUnit $colorMode={colorMode}>
                    {item.unit}
                  </CreditUnit>
                </ItemValue>
              </ItemRow>
            ))}
          </CardContent>
        </Card>
      ))}
    </CardsContainer>
  );
};

export default CreditsGrid;